import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/** Community (backend routes/community.ts). */
export type Scope = 'organisation' | 'educaitor';
export type Kind = 'post' | 'announcement' | 'shoutout' | 'achievement';

export interface Post {
  id: string;
  kind: Kind;
  scope: Scope;
  title: string | null;
  content: string;
  createdAt: string;
  pinned: boolean;
  author: { id: string; name: string; role: string; avatarUrl: string | null; team?: boolean };
  mentions: { id: string; name: string }[];
  badge: { name: string; tier: string } | null;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
  canComment: boolean;
  canDelete: boolean;
  canPin: boolean;
  mine: boolean;
}

export interface Comment {
  id: string;
  content: string;
  createdAt: string;
  author: { id: string; name: string; role: string; avatarUrl: string | null; team?: boolean };
  mentions?: { id: string; name: string }[];
  canDelete: boolean;
  mine: boolean;
}

type Feed = { data: Post[]; hasMore: boolean; page: number; organisation: string | null; canAnnounce: boolean; canPost: boolean; agreed: boolean; hiddenPeople: number };

export function useFeed(scope: Scope) {
  return useInfiniteQuery({
    queryKey: ['community', scope],
    queryFn: ({ pageParam }) => api<Feed>(`/community/feed?scope=${scope}&page=${pageParam}`),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });
}

export function usePostThread(id: string | null) {
  return useQuery({
    queryKey: ['community-post', id],
    queryFn: () => api<{ post: Post; comments: Comment[] }>(`/community/posts/${id}`),
    enabled: !!id,
  });
}

export function useColleagues() {
  return useQuery({
    queryKey: ['community-people'],
    queryFn: async () => (await api<{ data: { id: string; name: string }[] }>('/community/people')).data,
  });
}

/** Organisations an EducAitor admin can @-tag in the EducAitor channel. */
export function useTaggableOrganisations(enabled: boolean) {
  return useQuery({
    queryKey: ['community-organisations'],
    queryFn: async () => (await api<{ data: { id: string; name: string }[] }>('/community/organisations')).data,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCommunityAction() {
  const qc = useQueryClient();
  const refresh = () =>
    Promise.all([qc.invalidateQueries({ queryKey: ['community'] }), qc.invalidateQueries({ queryKey: ['community-post'] })]);
  return {
    post: useMutation({
      mutationFn: (input: { kind: Kind; scope: Scope; title?: string; content: string; mentions?: string[] }) =>
        api<{ id: string }>('/community/posts', { method: 'POST', body: JSON.stringify(input) }),
      onSuccess: refresh,
    }),
    like: useMutation({ mutationFn: (id: string) => api(`/community/posts/${id}/like`, { method: 'POST' }), onSuccess: refresh }),
    remove: useMutation({ mutationFn: (id: string) => api(`/community/posts/${id}`, { method: 'DELETE' }), onSuccess: refresh }),
    pin: useMutation({
      mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => api(`/community/posts/${id}/pin`, { method: 'POST', body: JSON.stringify({ pinned }) }),
      onSuccess: refresh,
    }),
    comment: useMutation({
      mutationFn: ({ id, content, mentions }: { id: string; content: string; mentions?: string[] }) =>
        api(`/community/posts/${id}/comments`, { method: 'POST', body: JSON.stringify({ content, mentions }) }),
      onSuccess: refresh,
    }),
    removeComment: useMutation({ mutationFn: (id: string) => api(`/community/comments/${id}`, { method: 'DELETE' }), onSuccess: refresh }),
    agree: useMutation({ mutationFn: () => api('/community/agree', { method: 'POST' }), onSuccess: refresh }),
    hide: useMutation({
      mutationFn: (userId: string) => api('/community/blocks', { method: 'POST', body: JSON.stringify({ userId }) }),
      onSuccess: refresh,
    }),
    report: useMutation({
      mutationFn: (input: { postId?: string; commentId?: string; reason?: string }) => api('/community/report', { method: 'POST', body: JSON.stringify(input) }),
    }),
  };
}

export const timeAgo = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
